/* eslint-disable no-magic-numbers */
export * from './memoize-async';
export * from './records';

/**
 * Wraps a function with a debouncer, which defers the action for a certain amount of time
 * and cancels that action if another call to the function is made during that time.
 *
 * @param action The action to debounce
 * @param delay The delay in milliseconds
 * @returns A debounced version of the function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<F extends (...args: any[]) => void>(
  action: F,
  delay: number,
): (...args: Parameters<F>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<F>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      action(...args);
      timeoutId = undefined;
    }, delay);
  };
}

export function formatNumber(num: number) {
  const array = num.toString().split('');
  let index = -3;
  while (array.length + index > 0) {
    array.splice(index, 0, ',');
    // Decrement by 4 since we just added another unit to the array.
    index -= 4;
  }
  return array.join('');
}

export function fromQueryString(
  qs: string = '',
  makeCaseInsensitive = true,
): Record<string, string> {
  //a new object
  const dct = {} as Record<string, string>;

  //if no query string, return the empty object
  if (!qs) return dct;

  //transfer params from the query string to the data object
  qs.split('&').forEach(function (item) {
    if (item.length > 0) {
      //split the key=value into named variables
      const [key, value] = item.split('=');

      if (!key || value === undefined) {
        throw new Error(`Failed to parse query string`);
      }

      //if making case sensitive (by which we mean making a copy of each key in camelCase)
      if (makeCaseInsensitive) {
        //what the key would be in camelCase and normalizing the various "ID", "id", etc. formats to "Id"
        dct[toCamelCase(key).replace(/id/gi, 'Id')] = value;
      } else {
        dct[key] = value;
      }
    }
  });

  //return the object
  return dct;
}

export function toQueryString(qs: Record<string, string | number>): string {
  const arr: string[] = [];

  Object.keys(qs).forEach(function (key) {
    arr.push(`${key}=${qs[key]}`);
  });

  return arr.join('&');
}

function toCamelCase(s: string) {
  return s
    .replace(/\s(.)/g, function ($1) {
      return $1.toUpperCase();
    })
    .replace(/\s/g, '')
    .replace(/^(.)/, function ($1) {
      return $1.toLowerCase();
    });
}

/**
 * Waits for the test function to return true
 * example usage:
 *    wait for an element to exist, then save it to a variable
 *        let el = await waitFor(() => document.querySelector('#el_id')))
 *    timeout_ms and frequency are optional parameters
 */
export async function waitFor(
  test: () => boolean,
  timeout_ms = 10000,
  frequency = 100,
) {
  //if (typeof (test) != "function") throw new Error("test should be a function in waitFor(test, [timeout_ms], [frequency])")
  //if (typeof (timeout_ms) != "number") throw new Error("timeout argument should be a number in waitFor(test, [timeout_ms], [frequency])");
  //if (typeof (frequency) != "number") throw new Error("frequency argument should be a number in waitFor(test, [timeout_ms], [frequency])");

  //let last = Date.now();
  //let logPassed = () => console.log('Passed: ', test);
  //let logTimedout = () => console.log('%c' + 'Timeout : ' + test, 'color:#cc2900');
  //let logWaiting = () =>
  //{
  //    if (Date.now() - last > 1000)
  //    {
  //        last = Date.now();
  //        console.log('%c' + 'waiting for: ' + test, 'color:#809fff');
  //    }
  //}

  const endTime = Date.now() + timeout_ms;
  //let isNotTruthy = (val) => val === undefined || val === false || val === null || val.length === 0; // for non arrays, length is undefined, so != 0
  let result = test();
  //while (isNotTruthy(result))
  while (!result) {
    if (Date.now() > endTime) {
      //logTimedout();
      return false;
    }
    //logWaiting();
    await sleep(frequency);
    result = test();
  }
  //logPassed();
  return result;
}

/**
 * Returns a promise that resolves after the specified amount of time.
 * @param ms The amount of time to wait before resolving, in milliseconds.
 * @returns
 */
export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function parseUrl(url: string): { base: string; query?: string } {
  const parts = url.split('?');

  return { base: parts[0]!, query: parts[1]! };
}

export function isArray(value: object): value is readonly unknown[] {
  return Array.isArray(value);
}

export function isIterable(value: object): value is Iterable<unknown> {
  return typeof (value as Iterable<unknown>)[Symbol.iterator] === 'function';
}
